import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { Subtask } from "@/hooks/use-categories";
import { useAddSubtask, useUpdateSubtask } from "@/hooks/use-categories";
import type { AddSubtaskInput, UpdateSubtaskInput } from "@/schemas/categories";
import { AddSubtaskSchema, UpdateSubtaskSchema } from "@/schemas/categories";

// ── New Subtask ────────────────────────────────────────────────────────────────

interface NewSubtaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoryId: string;
  categoryName: string;
}

export function NewSubtaskDialog({
  open,
  onOpenChange,
  categoryId,
  categoryName,
}: NewSubtaskDialogProps) {
  const addSubtask = useAddSubtask();
  const form = useForm<AddSubtaskInput>({
    resolver: zodResolver(AddSubtaskSchema),
    defaultValues: { name: "", active: true },
  });

  function onSubmit(values: AddSubtaskInput) {
    addSubtask.mutate(
      { categoryId, ...values },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Subtask</DialogTitle>
          <DialogDescription>
            Add a subtask to <strong className="text-foreground">{categoryName}</strong>.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Preliminary Report" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addSubtask.isPending}>
                {addSubtask.isPending ? "Adding..." : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Subtask ───────────────────────────────────────────────────────────────

interface EditSubtaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subtask: Subtask;
}

export function EditSubtaskDialog({ open, onOpenChange, subtask }: EditSubtaskDialogProps) {
  const updateSubtask = useUpdateSubtask();
  const form = useForm<UpdateSubtaskInput>({
    resolver: zodResolver(UpdateSubtaskSchema),
    defaultValues: { name: subtask.name },
  });

  useEffect(() => {
    if (open) {
      form.reset({ name: subtask.name });
    }
  }, [open, subtask, form]);

  function onSubmit(values: UpdateSubtaskInput) {
    updateSubtask.mutate({ id: subtask.id, ...values }, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Subtask</DialogTitle>
          <DialogDescription>Update the subtask name.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateSubtask.isPending}>
                {updateSubtask.isPending ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
